import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SAMPLE_QUERIES = {
  en: {
    lang: 'English',
    short: 'EN',
    query: "Mom is on Cycle 3 of mFOLFOX6. Her fingers are tingling and sensitive to cold drinks. What can she safely eat tonight?",
    answer: "Cold-induced dysesthesia is common with oxaliplatin. Serve warm or room-temperature foods, avoid ice, and use insulated gloves when handling chilled items.",
    doctorNote: "Ask your oncologist: 'Does the cold sensitivity in her fingers warrant adjusting the oxaliplatin dose?'"
  },
  hi: {
    lang: 'हिंदी',
    short: 'HI',
    query: "माँ का mFOLFOX6 का तीसरा साइकिल चल रहा है। उँगलियों में झुनझुनी और ठंडी चीजों से संवेदनशीलता है। आज रात क्या खिलाएं?",
    answer: "ऑक्सालिप्लेटिन के कारण ठंड से संवेदनशीलता सामान्य है। हमेशा गुनगुना या सामान्य तापमान का भोजन दें और बर्फ से बचें।",
    doctorNote: "डॉक्टर से पूछें: 'क्या उँगलियों में ठंड की संवेदनशीलता के कारण ऑक्सालिप्लेटिन की खुराक बदलने की ज़रूरत है?'"
  },
  es: {
    lang: 'Español',
    short: 'ES',
    query: "Mamá está en el Ciclo 3 de mFOLFOX6. Siente hormigueo en los dedos y sensibilidad al frío. ¿Qué puede cenar hoy con seguridad?",
    answer: "La disestesia por frío es común con el oxaliplatino. Ofrezca alimentos a temperatura ambiente y evite bebidas heladas.",
    doctorNote: "Preguntar al oncólogo: '¿La sensibilidad al frío en sus dedos amerita ajustar la dosis de oxaliplatino?'"
  },
  bn: {
    lang: 'বাংলা',
    short: 'BN',
    query: "মায়ের mFOLFOX6 সাইকেল ৩ চলছে। আঙুলে অবশ ভাব ও ঠান্ডার অনুভূতি হচ্ছে। আজ রাতে কী খাবার দেওয়া নিরাপদ?",
    answer: "অক্সালিপ্ল্যাটিনের কারণে ঠান্ডায় সংवेदनশীলতা স্বাভাবিক। খাবার ও জল স্বাভাবিক তাপমাত্রায় দিন, ঠান্ডা জিনিস এড়িয়ে চলুন।",
    doctorNote: "ডাক্তারের কাছে প্রশ্ন: 'আঙুলে ঠান্ডার অনুভূতির কারণে কি অক্সালিপ্ল্যাটিনের মাত্রা কমানো দরকার?'"
  },
  mr: {
    lang: 'मराठी',
    short: 'MR',
    query: "आईचे mFOLFOX6 सायकल ३ चालू आहे. बोटांमध्ये मुंग्या येत आहेत आणि गार पाण्याची ऍलर्जी वाटते. आज रात्री काय जेवण द्यावे?",
    answer: "ऑक्सालिप्लेटिनमुळे थंडीची संवेदनशीलता सामान्य आहे. नेहमी कोमट अन्न द्या आणि फ्रीजमधील वस्तू थेट हाताळणे टाळा.",
    doctorNote: "डॉक्टरांना विचारा: 'बोटांमधील मुंग्यांमुळे ऑक्सालिप्लेटिनच्या डोसमध्ये बदल करण्याची गरज आहे का?'"
  }
};

export default function HowItWorksBento() {
  const [activeLang, setActiveLang] = useState('en');
  const activeSample = SAMPLE_QUERIES[activeLang] || SAMPLE_QUERIES.en;

  return (
    <section id="how" className="byo-bento-section">
      <div className="vani-wrap">
        {/* Apple/YC Style Section Header */}
        <div className="byo-bento-header">
          <div className="byo-pill-badge">
            <span className="byo-badge-dot" />
            <span>HOW VANI WORKS</span>
            <span className="byo-badge-sep">/</span>
            <span className="byo-pill-highlight">NO FORMS · 18 LANGUAGES</span>
          </div>
          <h2 className="byo-bento-title">
            Clear Answers to Everyday Questions, Grounded in Medicine.
          </h2>
          <p className="byo-bento-subtitle">
            No intake forms, drop-down menus, or diagnostic guesswork. Ask about symptoms, meals, or medications in any of 18 languages. Vani references verified oncology guidelines and remembers your care history from one conversation to the next.
          </p>
        </div>

        {/* Bento 3-Card Showcase Grid */}
        <div className="byo-bento-grid">
          
          {/* CARD 1: NATURAL MULTILINGUAL INPUT */}
          <div className="byo-card group">
            <div className="byo-card-top">
              <span className="byo-card-idx">01 // NATURAL CONVERSATION</span>
              <span className="byo-card-status">
                <span className="byo-pulse-green" />
                Voice & Text
              </span>
            </div>

            {/* Interactive Audio Waveform & Language Selector */}
            <div className="byo-sim-box">
              <div className="byo-audio-bar">
                <div className="byo-waveform">
                  {[40, 75, 55, 90, 60, 100, 70, 85, 45, 95, 65, 80, 50, 70, 85, 40].map((h, i) => (
                    <span
                      key={i}
                      className="byo-wave-bar"
                      style={{ height: `${h}%`, animationDelay: `${i * 0.08}s` }}
                    />
                  ))}
                </div>
                <div className="byo-mic-chip">
                  <span className="byo-pulse-green" />
                  <span>18 Native Languages</span>
                </div>
              </div>

              {/* Segmented Language Selector */}
              <div className="byo-lang-tabs" role="tablist" aria-label="Select sample language">
                {Object.keys(SAMPLE_QUERIES).map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setActiveLang(code)}
                    className={`byo-lang-tab ${activeLang === code ? 'is-active' : ''}`}
                  >
                    <span>{SAMPLE_QUERIES[code].short}</span>
                    <span className="byo-lang-name">{SAMPLE_QUERIES[code].lang}</span>
                  </button>
                ))}
              </div>

              {/* Simulated Query Bubble */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeLang}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.16 }}
                  className="byo-query-bubble"
                >
                  <div className="byo-query-sender">
                    <span className="byo-voice-icon">🎙️</span>
                    <span>Patient & Caregiver Voice Input</span>
                  </div>
                  <p className="byo-query-text">“{activeSample.query}”</p>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="byo-card-content">
              <h3 className="byo-card-title">Speak or write in your everyday words</h3>
              <p className="byo-card-text">
                Ask in the words you naturally use. Vani understands chemotherapy regimens, cancer staging, and lab results, while following everyday conversational phrasing in 18 native languages.
              </p>
              <div className="byo-tag-row">
                <span className="byo-tag">Voice & Text</span>
                <span className="byo-tag">Preserves Medical Terms</span>
                <span className="byo-tag">No Forms Required</span>
              </div>
            </div>
          </div>

          {/* CARD 2: EVIDENCE-BASED CLINICAL GUIDANCE */}
          <div className="byo-card group">
            <div className="byo-card-top">
              <span className="byo-card-idx">02 // CLINICAL GUIDANCE</span>
              <span className="byo-card-status">
                <span className="byo-pulse-emerald" />
                Oncologist Vetted
              </span>
            </div>

            {/* Simulated Clinical Synthesis Output */}
            <div className="byo-sim-box">
              <div className="byo-telemetry-row">
                <span className="byo-telemetry-item">
                  <span className="byo-check-dot">✓</span>
                  NCCN Guidelines
                </span>
                <span className="byo-telemetry-item">
                  <span className="byo-check-dot">✓</span>
                  ASCO Vetted
                </span>
                <span className="byo-telemetry-latency">⚡ 1.8s Response</span>
              </div>

              {/* Synthesized Response Box */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeLang}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.16 }}
                  className="byo-response-card"
                >
                  <div className="byo-response-header">
                    <span className="byo-response-badge">Clinical Guidance</span>
                    <span className="byo-conf-tag">NCCN Aligned</span>
                  </div>
                  <p className="byo-response-body">{activeSample.answer}</p>
                  <div className="byo-doc-note">
                    <span className="byo-note-icon">📋</span>
                    <span><strong>Question for Doctor:</strong> {activeSample.doctorNote}</span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="byo-card-content">
              <h3 className="byo-card-title">Clear, calm answers for late-night worries</h3>
              <p className="byo-card-text">
                Short, practical answers written for 2:00 AM. We explain what to do at home in plain language, without overwhelming jargon, so you can walk into your next clinic visit with clear, focused questions.
              </p>
              <div className="byo-tag-row">
                <span className="byo-tag">NCCN & ASCO Aligned</span>
                <span className="byo-tag">Plain Language</span>
                <span className="byo-tag">Doctor Discussion Points</span>
              </div>
            </div>
          </div>

          {/* CARD 3: CARE CONTINUITY */}
          <div className="byo-card group">
            <div className="byo-card-top">
              <span className="byo-card-idx">03 // CARE CONTINUITY</span>
              <span className="byo-card-status">
                <span className="byo-pulse-indigo" />
                Remembers Your Care
              </span>
            </div>

            {/* Longitudinal Memory Graph Widget */}
            <div className="byo-sim-box">
              <div className="byo-memory-card">
                <div className="byo-mem-header">
                  <span className="byo-mem-title">
                    <span className="byo-lock-icon">🔒</span>
                    Your Care History
                  </span>
                  <span className="byo-mem-chip">Private & Encrypted</span>
                </div>
                <div className="byo-mem-items">
                  <div className="byo-mem-row">
                    <span className="byo-mem-k">DIAGNOSIS</span>
                    <span className="byo-mem-v">Colorectal Adenocarcinoma · Stage III</span>
                  </div>
                  <div className="byo-mem-row">
                    <span className="byo-mem-k">REGIMEN</span>
                    <span className="byo-mem-v highlight">mFOLFOX6 · Cycle 3 of 12</span>
                  </div>
                  <div className="byo-mem-row">
                    <span className="byo-mem-k">CAREGIVER</span>
                    <span className="byo-mem-v">Daughter (Primary Coordinator)</span>
                  </div>
                  <div className="byo-mem-row">
                    <span className="byo-mem-k">OBSERVATION</span>
                    <span className="byo-mem-v text-emerald-800 font-medium">Tracking Nadir & Cold Sensitivity</span>
                  </div>
                </div>
                <div className="byo-timeline-bar">
                  <span className="byo-timeline-step done">Cycle 1 ✓</span>
                  <span className="byo-timeline-step done">Cycle 2 ✓</span>
                  <span className="byo-timeline-step current">Cycle 3 Active</span>
                  <span className="byo-timeline-step future">Cycle 4 →</span>
                </div>
              </div>
            </div>

            <div className="byo-card-content">
              <h3 className="byo-card-title">We remember your story, so you do not repeat it</h3>
              <p className="byo-card-text">
                Vani keeps track of your diagnosis, chemotherapy regimen, cycle milestones, and doctor instructions. Every conversation picks up right where you left off, so you never have to re-explain everything when you are exhausted.
              </p>
              <div className="byo-tag-row">
                <span className="byo-tag">Remembers Your Journey</span>
                <span className="byo-tag">Never Repeat Yourself</span>
                <span className="byo-tag">HIPAA-Ready Privacy</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
